import { Draggable } from "@msuite/ui/dnd";
import { cva, type VariantProps } from "@msuite/ui/libs/cva";
import { useQueryClient, useQueryKey } from "@msuite/ui/query";
import { cn, responseHandler } from "@msuite/ui/utils";
import type { Enums, Tables, TicketResourceLink } from "@msuite/utils/types";
import { type JSX, useState } from "react";
import { NoteText } from "@/components/notes";
import { client } from "@/hono";
import { TicketAssignee } from "./ticket-assignee";
import { TicketCardContextMenu } from "./ticket-card-context-menu";
import { TicketCardDate } from "./ticket-card-date";
import { TicketCardFiles } from "./ticket-card-files";
import { TicketCardResourceLinks } from "./ticket-card-resource-links";
import { TicketTypeIcon } from "./ticket-type-icon";

export type TicketVariants = Omit<VariantProps<typeof ticketCard>, "state" | "color">;
export type ExtendTicketCardProps = Pick<
	TicketCardProps,
	"action" | "leftBorder" | "getColor" | "withContextMenu"
>;

/** Props Interface */
export interface TicketCardProps extends TicketVariants {
	ticket: Tables<"tickets"> & {
		ticketFiles?: (Tables<"ticketFiles"> & {
			file: Tables<"files">;
		})[];
		projectResourceLinks?: TicketResourceLink[];
		// A ticket synced through the board queries carries its type row — on a guest board the
		// host's, which `TicketTypeIcon` cannot look up in the viewer's catalogue.
		ticketType?: Pick<Tables<"ticketTypes">, "name" | "icon" | "color"> | null;
	};
	isDraggable?: boolean;
	action?: (ticket: Tables<"tickets">) => JSX.Element;
	getAccentColor?: (ticket: Tables<"tickets">) => Enums<"color"> | null;
	onCompleteEdit?: () => Promise<void> | void;
	getColor?: (ticket: Tables<"tickets">) => VariantProps<typeof ticketCard>["color"] | null;
	/**
	 * Whether the card offers its context menu. A guest board shows the host's tickets
	 * read-only, and the menu is an edit surface — every entry would fail against a foreign
	 * ticket.
	 */
	withContextMenu?: boolean;
}

export function TicketCard({
	ticket,
	isDraggable,
	action,
	leftBorder = "default",
	variant,
	onCompleteEdit,
	getColor,
	withContextMenu = true,
}: TicketCardProps) {
	/** State */
	const [_willUnmount, _setWillUnmount] = useState<boolean>(false);
	const qc = useQueryClient();
	const prefetchProjectQueryKey = useQueryKey(["projects", ticket.projectId]);
	const prefetchTicketQueryKey = useQueryKey(["tickets", ticket.id]);

	const classes = ticketCard({
		leftBorder,
		state: ticket.state,
		variant,
		color: getColor?.(ticket) || undefined,
	});

	async function prefetch() {
		await Promise.all([prefetchProject(), prefetchTicket()]);
	}

	async function prefetchProject() {
		await qc.prefetchQuery({
			queryKey: prefetchProjectQueryKey,
			queryFn: async () => {
				const response = await client.projects[":projectId"].$get({
					param: {
						projectId: ticket.projectId,
					},
				});
				const json = await responseHandler(response);
				return json.project;
			},
		});
	}

	async function prefetchTicket() {
		await qc.prefetchQuery({
			queryKey: prefetchTicketQueryKey,
			queryFn: async () => {
				const response = await client.tickets[":ticketId"].$get({
					param: {
						ticketId: ticket.id,
					},
				});
				const data = await responseHandler(response);
				return data.ticket;
			},
		});
	}

	/** Render */
	const card = (
		<Draggable
			type="ticket"
			item={ticket}
			className={cn(classes)}
			style={{
				contentVisibility: "auto",
			}}
			isDisabled={!isDraggable}
			onDragStart={prefetch}
			data-ticket-id={ticket.id}
			data-ticket-type-id={ticket.type}
			data-ticket-description={ticket.description}
			// What `TicketFileDrop` walks up to when a file is dragged over the board: the three ids
			// its link needs, published here because the drop is resolved from the DOM rather than
			// from a dropzone of the card's own. A card with no row behind it — a ticket queued in
			// the new-project form — publishes nothing and takes no drop.
			data-file-drop-ticket={ticket.id}
			data-file-drop-project={ticket.projectId}
			data-file-drop-org={ticket.orgId}
		>
			<div
				className={cn(
					"flex-1 min-w-0",
					"items-center gap-x-2 gap-y-1 grid grid-cols-2",
					"[&>*:not(:is(.ticket-icon))]:col-start-2",
				)}
				style={{
					gridTemplateColumns: "max-content 1fr",
				}}
			>
				<div className="translate-y-0.5 ticket-icon">
					<TicketTypeIcon
						ticketTypeId={ticket.type}
						ticketType={ticket.ticketType}
					/>
				</div>
				<div className="h-stack min-w-0">
					<TicketAssignee ticket={ticket} />
					<p className="text-primaryGray-700 dark:text-primaryGray-200 truncate">
						{ticket.description}
					</p>
				</div>
				{/* Both indicators render null when they have nothing to show, so the row
				    collapses on `empty:hidden` instead of leaving a blank grid line. */}
				<div className="h-stack flex-wrap empty:hidden">
					<TicketCardFiles
						ticketId={ticket.id}
						projectId={ticket.projectId}
						ticketFiles={ticket.ticketFiles ?? []}
					/>
					<TicketCardResourceLinks resourceLinks={ticket.projectResourceLinks ?? []} />
				</div>
				{(ticket.startDate || ticket.endDate) && <TicketCardDate ticket={ticket} />}
				{ticket.note && (
					<NoteText
						note={ticket.note}
						className="text-xs"
					/>
				)}
			</div>
			{action && <div className="shrink-0 empty:hidden">{action(ticket)}</div>}
		</Draggable>
	);

	if (!withContextMenu) return card;

	return (
		<TicketCardContextMenu
			ticket={ticket}
			onCompleteEdit={onCompleteEdit}
		>
			{card}
		</TicketCardContextMenu>
	);
}

const ticketCard = cva(
	[
		"ticket-card",
		"bg-primaryGray-50 dark:bg-primaryGray-800/50 mb-1 px-2 py-2 border-[0.5px] rounded-p-1.5",
		"border-color",
		"transition-colors",
		"text-sm",
		"h-stack gap-4",
		// The card a file is about to land on, marked by `TicketFileDrop` while the drag hangs over
		// it. Border and ring rather than a covering layer: the same shape `DragOverIndicator` draws
		// for a dragged ticket, so both kinds of drag say "here" in one language, and the card's own
		// text stays legible underneath the drag that is about to change it.
		"data-[file-drop=active]:border-accent-500! data-[file-drop=active]:border-solid!",
		"data-[file-drop=active]:bg-accent-500/5! data-[file-drop=active]:ring-2",
		"data-[file-drop=active]:ring-accent-500/10",
	],
	{
		variants: {
			variant: {
				default: "",
				plain: "border-transparent! bg-transparent! p-0!",
			},
			leftBorder: {
				thick: "border-l-8 border-l-solid!",
				default: "",
			},
			state: {
				active: "border-solid",
				inactive: "border-dashed",
				archived: "border-dashed",
			},
			color: {
				amber: "border-l-amber-500!",
				emerald: "border-l-emerald-500!",
			},
		},
		defaultVariants: {
			leftBorder: "default",
			state: "active",
			variant: "default",
		},
	},
);
